<script setup lang="ts">
import { formatAmountInput } from "@/utils/format";
import { computed, defineProps } from "vue";
import TariffMainOption from "@/components/tariff/TariffMainOption.vue";
import { PropType } from "@vue/runtime-core";
import { TariffOptionStruct } from "@/struct/TariffStruct";
import { useStore } from "@/store";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  amount: {
    type: Number,
    default: 0,
  },
  oldAmount: {
    type: Number,
    default: 0,
  },
  /** Промо-скидка в ПРОЦЕНТАХ (100 === 100%), не в рублях. */
  discountPercent: {
    type: Number,
    default: 0,
  },
  discountName: {
    type: String,
    default: "",
  },
  nextDate: {
    type: String,
    default: "",
  },
  /** Готовая подпись к цене ("за месяц" / "за квартал" / "за полгода" / "за год"). */
  periodLabel: {
    type: String,
    default: "",
  },
  options: {
    type: Array as PropType<TariffOptionStruct[]>,
    required: true,
  },
});

const store = useStore();
const mainTariff = computed(() => store.getters["tariff/mainTariff"]);
const { t } = useI18n();

const isBlocked = computed(() => {
  return props.options.findIndex((item) => item.value !== 0) === -1;
});

// Зачёркиваем старую цену только если она действительно выше текущей —
// иначе при промо без реальной разницы получалось "3 000 ₽ 3 000 ₽".
const hasDiscount = computed(
  () => props.discountPercent > 0 && props.oldAmount > props.amount
);

const displayAmount = computed(() =>
  hasDiscount.value ? props.amount : props.oldAmount
);

const periodText = computed(
  () => props.periodLabel || t("common.labels.perMonth")
);

const classes = computed(() => {
  return [isBlocked.value ? "--blocked" : ""];
});
</script>

<template>
  <div class="tariff-main" :class="classes">
    <div class="tariff-main__wrapper">
      <div class="tariff-main__header">
        <router-link to="tariff">
          <BaseFigureForIcon
            class="tariff-main__header-more"
            icon="more"
            :color="isBlocked ? 'secondary' : 'white-opacity-40'"
            size="small"
            is-button
          />
        </router-link>
        <p
          class="tariff-main__header-title h3"
          :class="{ 'color-white': !isBlocked }"
        >
          {{ mainTariff?.title }}
        </p>
        <div
          v-if="!isBlocked"
          class="tariff-main__header-amount h3 normal color-white"
        >
          <span class="tariff-main__header-amount__number">
            {{ formatAmountInput(displayAmount) }} ₽
          </span>
          <span
            v-if="hasDiscount"
            class="tariff-main__header-amount__number --old"
          >
            {{ formatAmountInput(oldAmount) }} ₽
          </span>
          <span>{{ periodText }}</span>
        </div>
        <p
          v-if="!isBlocked && discountPercent"
          class="tariff-main__header-description caption-small color-text-addons"
        >
          {{ discountName }}
        </p>
        <p
          v-else-if="isBlocked && !discountPercent"
          class="tariff-main__header-description caption-medium color-grey-light"
        >
          {{ t("tariff.main.unavailableLine1") }}<br />
          {{ t("tariff.main.unavailableLine2") }}
        </p>
      </div>

      <div class="tariff-main__options">
        <TariffMainOption
          v-for="item in options"
          :key="item.id"
          :option="item"
        />
      </div>
      <div
        v-if="!isBlocked"
        class="tariff-main__next-date color-white caption-small"
      >
        <span>{{ t("tariff.main.nextCharge", { date: nextDate }) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/style/main";

.tariff-main {
  height: 100%;
  border-radius: 32px;
  background-color: var(--color-white);
  background: var(--gradient-main);
  //min-height: 200px;

  &.--blocked {
    background: var(--color-white);
  }

  &__wrapper {
    padding: 20px 20px 29px 23px;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;

    @media (max-width: 768px) {
      //height: 245px;
    }
  }

  &__header {
    margin-bottom: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-right: 34px;

    &-more {
      position: absolute;
      right: 22px;
      top: 20px;
    }

    &-amount {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 4px;

      &__number {
        &.--old {
          text-decoration: line-through;
        }
      }
    }
    &-description {
      margin-bottom: 28px;
      min-height: 36px;
    }
  }

  &__options {
    display: flex;
    flex-direction: row;
    gap: 12px;
  }

  &__next-date {
    margin-top: 16px;
  }
}
</style>
